import React from 'react';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { AssetTreeView } from '../../../types/app/assetTreeView.types';
import { dateShowFormat, utcFormat } from '../../../types/core/date.types';
import * as machineDataApi from '../../../api/app/machineDataApi';
import { Loader } from '../../../components/core/Loader/Loader';
import { generateParentNames } from '../../../utils/core/functions/assetTreeView.fuctions';
import {
  checkAndFillMissingValues,
  getValByGranularity,
  prepareDataForExport,
  testDataProcess,
} from '../../../utils/core/functions/dataProcess.functions';
import { convertGranularityToMinutes } from '../../../utils/core/functions/convertDateTime';
import { Timezone } from '../../../components/app/TimezoneChanger/TimezoneChanger';
import { DashboardTable } from '../../../components/app/DashboardTable/DashboardTable';
import { dateTimeShowFormat, hoursMinutesShowFormat } from '../../../types/core/date.types';
import { ChartTypes, ChartDataSet } from '../../../types/app/chart.types';
import { Button, Grid, Typography } from '@mui/material';
import { useTranslation } from '../../../contexts/translation';
import { useStyles } from './DashboardCycleTimeHistory.styles';
import { ChartsContainerCycleTime } from '../../Charts/ChartsContainerCycleTime';
import { useLocalTheme } from '../../../hooks/useLocalTheme';

interface Props {
  dateFrom: Moment;
  dateTo: Moment;
  assetTreeViewData: AssetTreeView;
  selectedAssets: AssetTreeView[];
  timezone: Timezone;
  granularity: string;
}

export function DashboardCycleTimeHistoryDataLoader({
  dateFrom,
  dateTo,
  assetTreeViewData,
  selectedAssets,
  timezone,
  granularity,
}: Props) {
  const { localTheme } = useLocalTheme();
  const { t, lang } = useTranslation();
  const classes = useStyles();
  const [assetTreeViewDataCustom, setAssetTreeViewDataCustom] = useState<AssetTreeView | null>();
  const [cycleTime, setCycleTime] = useState<any>(null);
  const [dataComponentCycleTime, setDataComponentCycleTime] = useState<'chart' | 'table'>('chart');
  const [proccessedData, setProccessedData] = useState<any>(null);
  const [isLoading, setLoading] = useState<boolean>(false);

  // Rekurzivně doplníme parent names do stromu assetů
  useEffect(() => {
    if (!assetTreeViewData) return;
    setAssetTreeViewDataCustom(generateParentNames(assetTreeViewData, ''));
  }, [assetTreeViewData]);

  // Při změně filtru (assetu nebo datumu), se projde celé asset tree view, a pro každý asset se načtou datasety
  useEffect(() => {
    if (!selectedAssets || !dateFrom || !dateTo) return;
    setLoading(true);

    const getAndSetCycleTimesForAssetTreeView = async () => {
      const resultsCycleTimes = await Promise.all(
        selectedAssets.map(async (asset) => {
          let cycleTime = await machineDataApi.readCycleTimeFilterByDate({
            machine: asset.influxMachineName,
            dateFrom: dateFrom.clone().format(utcFormat),
            dateTo: dateTo.clone().format(utcFormat),
            // influx vrací špatně posunuté hodnoty pro granularitu 2h a víc => posílat 1h a pak přepočítat
            aggregation: convertGranularityToMinutes(granularity) > 60 ? '1h' : granularity,
          });
          // přepočet hodnot v případě vyšší granularity než 1h
          if (convertGranularityToMinutes(granularity) > 60 && cycleTime.data.length > 0)
            cycleTime.data[0].values = getValByGranularity(
              checkAndFillMissingValues(cycleTime.data[0].values, dateFrom, dateTo),
              convertGranularityToMinutes(granularity),
              timezone,
            );
          cycleTime = { ...cycleTime, id: asset.id };
          return cycleTime;
        }),
      );

      setCycleTime(resultsCycleTimes);
    };
    getAndSetCycleTimesForAssetTreeView();
  }, [selectedAssets, dateFrom, dateTo]);

  useEffect(() => {
    if (!cycleTime || cycleTime.length === 0) {
      setProccessedData(null);
      return;
    }

    let data: any[] = [];
    let configs: any[] = [];
    cycleTime.map((ct: any) => {
      ct.data.map((dataSet: any) => {
        const iFrom = data[data.length - 1]
          ? data[data.length - 1].length + configs[data.length - 1].iFrom
          : 0;
        const iTo = iFrom + dataSet.values.length - 1;
        configs = [
          ...configs,
          {
            iFrom: iFrom,
            iTo: iTo,
            dataSetId: dataSet.dataId + '_' + ct.id,
            dataSetValueKey: 'value',
          },
        ];
        data = [...data, dataSet.values];
      });
    });

    const _proccessedData = testDataProcess({
      title: '',
      data: [].concat(...data),
      intervals: [
        {
          start: dateFrom.clone().add(convertGranularityToMinutes(granularity), 'minutes'),
          end: dateTo.clone(),
        },
      ],
      configs: configs,
      chartsConfig: [],
      granularity: convertGranularityToMinutes(granularity),
      timezone: timezone.value,
    });

    setProccessedData(_proccessedData);
    setLoading(false);
  }, [cycleTime, lang]);

  // Tabulkový pohled na cycleTime
  const dashboardTableCycleTime = useMemo(() => {
    if (isLoading) return <Loader />;
    if (!proccessedData || dataComponentCycleTime === 'chart') return;

    const isSameDay = proccessedData[0].timeObject.isSame(
      proccessedData[proccessedData.length - 1].timeObject.clone().add(-1, 'seconds'),
      'day',
    );
    const minToSubstract = -1 * convertGranularityToMinutes(granularity);
    const headers = proccessedData.map((item: any) => {
      const dateFrom = item
        .timeObject!.clone()
        .add(minToSubstract, 'minutes')
        .tz(timezone.value)
        .format(
          granularity === '24h'
            ? dateShowFormat
            : isSameDay
            ? hoursMinutesShowFormat
            : dateTimeShowFormat,
        );
      return {
        label: dateFrom,
        tooltip:
          dateFrom + ' - ' + item.timeObject!.tz(timezone.value).format(hoursMinutesShowFormat),
      };
    });

    let rows: any[] = [];
    selectedAssets.forEach((asset) => {
      rows = [
        ...rows,
        ...asset.cycleTimes.map((item) => {
          let rowSum = 0;
          return {
            label: `${item.parentAssetName} - ${item.name}`,
            data: proccessedData.map((value: any) => {
              value?.values.map((val: any) => {
                if (val.id === `${item.influxValueName}_${asset.id}`) {
                  rowSum = rowSum + val.value;
                }
              });
              return value.values.filter(
                (x: any) => x.id === `${item.influxValueName}_${asset.id}`,
              )[0]?.value;
            }),
            sum: rowSum,
            style: { '&:last-child': { fontWeight: 'bold' } },
          };
        }),
      ];
    });

    let sumOfColls: any = [];
    let sumOfSums: number = 0;
    for (let i = 0; i < proccessedData.length; i++) {
      let colSum = 0;
      rows.forEach((row) => {
        colSum += row.data[i] === undefined ? 0 : row.data[i];
      });
      sumOfSums += colSum;
      sumOfColls = [...sumOfColls, colSum];
    }
    rows = [
      ...rows,
      {
        label: '',
        data: sumOfColls,
        sum: sumOfSums,
        style: { fontWeight: 'bold' },
      },
    ];
    console.log(rows, 'rows');
    console.log(headers, 'headers');

    if (rows.length === 0) return <>{t('general.messages.error.no-data-to-display')}</>;

    return (
      <DashboardTable
        headers={headers}
        items={rows}
        isSameDay={isSameDay}
        exportOptions={{
          fileName: 'Cycle-time',
          data: prepareDataForExport(rows, headers),
        }}
      />
    );
  }, [proccessedData, dataComponentCycleTime, isLoading]);

  // Graf cycleTimes
  const dashboardChartCycleTimes = useMemo(() => {
    if (isLoading) return <Loader />;
    if (!proccessedData || dataComponentCycleTime === 'table') return;

    const isSameDay = proccessedData[0].timeObject.isSame(
      proccessedData[proccessedData.length - 1].timeObject.clone().add(-1, 'seconds'),
      'day',
    );

    const labels = proccessedData.map((item: any) => {
      return item
        .timeObject!.tz(timezone.value)
        .format(
          granularity === '24h'
            ? dateShowFormat
            : isSameDay
            ? hoursMinutesShowFormat
            : dateTimeShowFormat,
        );
    });

    let chartsConfig: any = [];
    let isRed = true;
    let isBlue = true;
    let colorCounter = 0;
    selectedAssets.forEach((asset) => {
      chartsConfig = [
        ...chartsConfig,
        ...asset.cycleTimes.map((item) => {
          let r = 50;
          const g = 50;
          let b = 50;

          if (isRed && !isBlue) r = 255 - 30 * colorCounter;
          else if (isRed && isBlue) {
            r = 255 - 30 * colorCounter;
            b = 255 - 30 * colorCounter;
          } else {
            r = 0;
            if (!isBlue) {
              colorCounter = 0;
              isBlue = true;
            }
            b = 255 - 30 * colorCounter;
          }

          if (r < 50) isRed = false;
          colorCounter++;
          return {
            dataSetId: item.influxValueName + '_' + asset.id,
            label: `${item.parentAssetName} - ${item.name}`,
            type: ChartTypes.LINE,
            style: {
              backgroundColor: {
                r: r,
                g: g,
                b: b,
              },
              borderColor: `rgb(${r},${g},${b})`,
              borderWidth: 2,
              fill: false,
            },
          };
        }),
      ];
    });

    let chartsData: any[] = [];
    chartsData = chartsConfig!.map((config: any) => {
      return {
        ...config,
        dataSetId: config.dataSetId,
        chartType: config.type,
        data: [
          ...proccessedData.map((item: any) => {
            const datasetValue = item.values.filter((x: any) => x.id === config.dataSetId)[0];
            if (datasetValue) return datasetValue.value;
          }),
        ],
        colors: [
          ...proccessedData.map((item: any) => {
            if (config.style.backgroundColor) return config.style.backgroundColor;
            if (config.type === ChartTypes.LINE) return { r: 51, g: 153, b: 255 };
            const value = item.values.filter((x: any) => x.id === config.dataSetId)[0].value;
            return 1300 > value ? { r: 255, g: 0, b: 0 } : { r: 10, g: 230, b: 0 };
          }),
        ],
      };
    });

    const chartsDataSets: ChartDataSet[] = chartsData.map((item) => {
      return {
        chartType: item.chartType,
        style: item.style,
        label: item.label,
        colors: item.colors,
        x: labels,
        y: item.data,
      };
    });

    if (chartsDataSets.length === 0) return <>{t('general.messages.error.no-data-to-display')}</>;

    console.log('proccessedData', proccessedData);
    console.log('chartsDataSets', chartsDataSets);
    console.log('chartsData', chartsData);
    console.log('chartsConfig', chartsConfig);
    return (
      <ChartsContainerCycleTime
        chartData={chartsDataSets}
        labels={labels}
        chartHeight={'400px'}
        chartTitle={t('page.dashboard.history.cycle-time.chart-title-1')}
        localTheme={localTheme}
      />
    );
  }, [proccessedData, dataComponentCycleTime, isLoading]);

  if (!assetTreeViewDataCustom || !cycleTime) return <Loader />;

  if (selectedAssets.length === 0)
    return <Typography variant={'h5'}>{t('page.dashboard.history.dataset-no-asset')}</Typography>;

  return (
    <Grid container>
      <Grid item xs={12} mt={2}>
        <Typography variant={'h5'} className={classes.datasetHeadline}>
          {t('page.dashboard.history.cycle-time.dataset-headline-3')}
        </Typography>
        <Button
          onClick={() =>
            setDataComponentCycleTime(dataComponentCycleTime === 'table' ? 'chart' : 'table')
          }
        >
          {dataComponentCycleTime === 'table'
            ? t('page.dashboard.history.dataset-view-switch-chart')
            : t('page.dashboard.history.dataset-view-switch-table')}
        </Button>
      </Grid>
      <Grid item xs={12}>
        {dataComponentCycleTime === 'table' ? dashboardTableCycleTime : dashboardChartCycleTimes}
      </Grid>
    </Grid>
  );
}
